import RiotControl from 'riotcontrol'
import logger from '../logger'
import {
  merge,
  cloneDeep,
  find,
} from 'lodash-es'
import extendedObservable from './base'
import api from '../api'
import LineItemUtils from '#util/utils/line-item-utils'
import AddressUtils from '#util/utils/address-utils'
import AjaxUtils from '../utils/ajax-utils'
import { OrderObservableInstance } from './order-observable'
import { ApplicationObservableInstance } from '../observables/application-observable'
import appProps from '../app-props'

const useByEads = appProps.get('checkout.audit.use.by.ead', false)

// /////////////
// Line Items //
// /////////////

class LineItems {
  constructor() {
    const orderResourceKey = 'lineItems'

    extendedObservable(this, 'lineItemsModel', [])

    const successHandler = (json, status, xhr) => {
      logger('Success: Communicated with OP Service - line items', 'info')()
      logger(json, 'info')()

      this.model = merge(this.model, json)
      RiotControl.trigger('updateOrderResource', orderResourceKey, this.model)

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      RiotControl.trigger('updateLineItemsSuccess')

      // Update the location in sessionStorage so if the customer goes back to the PDP or cart
      // the location in the tile shows the order address
      try {
        const { address } = this.orderModel.safeGet('getters.getOrderShippingAddress')
        sessionStorage.setItem('userLocation', JSON.stringify({
          city: address.city,
          postalCode: address.postalCode.slice(0, 5),
          state: address.state,
          countryCode: address.countryCode,
          latitude: null,
          longitude: null,
        }))
      } catch (err) {
        // something went wrong
        logger('error', err)()
      }

      return this.model
    }

    const errorHandler = (xhr, status, errorThrown) => {
      logger(`Error: Failed to communicate with OP Service - ${errorThrown}`, 'error')()
      logger(`Status: ${status}`, 'error')()
      logger(xhr, 'error')()

      // If checksum is invalid
      if (AjaxUtils.checkForBadChecksum(xhr)) {
        AjaxUtils.handleChecksumMismatch()
      }

      if (AjaxUtils.checkForNoOrder(xhr)) {
        AjaxUtils.handleNoOrder()
      }

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      RiotControl.trigger('updateLineItemsFailure')

      return xhr
    }

    const getRequestData = () => {
      const requestData = []

      // Only a small subset of the line item data, the 5 fields below, can
      // be sent to the Line Item Web Service or it may cause errors
      this.model.forEach((item) => {
        // Coupon items can NOT be sent in the payload or Line Item service will crap out
        if (!item.couponItem) {
          requestData.push({
            reiRefId: item.reiRefId,
            sku: item.sku,
            quantity: item.quantity,
            shipMethod: item.shipMethod,
            shippingAddress: item.shippingAddress,
          })
        }
      })

      return JSON.stringify(requestData)
    }

    // //////////////
    // Collection //
    // //////////////

    const updateLineItems = data => new Promise((resolve, reject) => {
      // Merge the changed data onto the order.
      this.model = LineItemUtils.smartMerge(this.model, data)

      api.lineItems.put(getRequestData())
        .then(successHandler)
        .then(resolve)
        .fail(errorHandler)
        .fail(reject)
    })

    // ////////////
    // Singular //
    // ////////////

    const deleteLineItem = id => new Promise((resolve, reject) => {
      api.lineItem.delete(id)
        .then(successHandler)
        .then(resolve)
        .fail(errorHandler)
        .fail(reject)
    })

    this.updateLineItemsWithAddress = (data) => {
      const shipItems = cloneDeep(this.orderModel.safeGet('getters.filteredShipLineItems'))
      const physicalGiftCards = cloneDeep(this.orderModel.safeGet('getters.filteredPhysicalGiftLineItems'))
      let transformedLineItems = []
      let transformedGiftCards = []

      if (shipItems.length) {
        const shipMethods = this.orderModel.safeGet('getters.filteredCommonShipMethods', [])
        const appliedShipMethod = this.orderModel.safeGet('getters.getSelectedShipMethod')
        let shipMethod = null

        // For BY we need to keep the ship method applied even if there are no common methods
        // in order to get fulfillment info via audit
        if (appliedShipMethod && ((useByEads && !shipMethods.length) || find(shipMethods, o => o.id === appliedShipMethod.id))) {
          shipMethod = appliedShipMethod
        } else if (shipMethods.length) {
          shipMethod = { id: shipMethods[0].id }
        } else {
          // reset method
          LineItemUtils.resetShipMethodOnLineItems(shipItems)
        }

        const shipData = Object.assign({}, data, { shipMethod })

        transformedLineItems = LineItemUtils.resetFulfillmentsOnLineItems(shipItems)
        transformedLineItems = LineItemUtils.resetAddressIdOnLineItems(shipItems)
        transformedLineItems = LineItemUtils.applyPayloadToLineItems(shipItems, shipData)
      }

      if (physicalGiftCards.length) {
        const giftShipMethods = this.orderModel.safeGet('getters.filteredPhysicalGiftShipMethods', [])
        const appliedGiftShipMethod = this.orderModel.safeGet('getters.getGiftShipMethod')
        let giftShipMethod = null

        if (appliedGiftShipMethod && find(giftShipMethods, o => o.id === appliedGiftShipMethod.id)) {
          giftShipMethod = appliedGiftShipMethod
        } else if (giftShipMethods.length) {
          giftShipMethod = { id: giftShipMethods[0].id }
        } else {
          // reset method
          LineItemUtils.resetShipMethodOnLineItems(physicalGiftCards)
        }

        const gcShipData = Object.assign({}, data, { shipMethod: giftShipMethod })

        transformedGiftCards = LineItemUtils.resetFulfillmentsOnLineItems(physicalGiftCards)
        transformedGiftCards = LineItemUtils.resetAddressIdOnLineItems(physicalGiftCards)
        transformedGiftCards = LineItemUtils.applyPayloadToLineItems(physicalGiftCards, gcShipData)
      }

      const modifiedPayload = transformedLineItems.concat(transformedGiftCards)

      return updateLineItems(modifiedPayload)
    }

    /**
     * Attempts to pre-apply a shipping address to the order.
     * This is done so that other BE calls to get fulfillment
     * and shipping charge information is done ahead of time
     * so that the user is not stuck waiting on the shipping
     * page while data is being fetched.
     * @return {none} no return
     */
    this.conditionallyUpdateShipping = () => {
      // If the order does not have a shipping address set
      // yet but there is a billing address and it is a
      // good address, set it as the shipping address.
      const allShipItemsHaveAddress = this.orderModel.safeGet('getters.allShipItemsHaveAddress')
      const isShippingAddressRequied = this.orderModel.safeGet('lineItems').length !== this.orderModel.safeGet('getters.filteredPickupLineItems').length
      const isGuest = this.orderModel.safeGet('user.isGuest')
      const isSameBillingAndShipping = ApplicationObservableInstance.model.sameBillingAndShipping

      // If guest and unchecked 'Billing same as Shipping'
      // Does not set shipping, opens shipping form
      if (isGuest && !isSameBillingAndShipping) return Promise.resolve()

      // Check to see if the order needs a shipping address
      if (!allShipItemsHaveAddress && isShippingAddressRequied) {
        return Promise.resolve()
          .then(() => this.orderModel.safeGet('getters.determineShippingAddress'))
          .then(AddressUtils.createShippingAddressPayload)
          .then(this.updateLineItemsWithAddress)
          .catch((err) => {
            // something went wrong
            logger('error', err)()
          })
      }

      return Promise.resolve()
    }


    // //////////////
    // Collection //
    // //////////////

    this.on('updateLineItems', (data, cb, fail) => {
      logger('updateLineItems', 'debug')()
      updateLineItems(data)
        .then(() => {
          RiotControl.trigger('updateAppModel', 'skipAuditOnTransition', true)
          return OrderObservableInstance.auditOrder()
        })
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          if (fail && typeof fail === 'function') fail(err)
        })
    })

    this.on('updateLineItemsWithAddress', (data, cb, fail) => {
      logger('updateLineItemsWithAddress', 'debug')()
      this.updateLineItemsWithAddress(data)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          if (fail && typeof fail === 'function') fail(err)
        })
    })

    this.on('update', (data) => {
      if (data.orderModel) {
        if (this.model !== data.orderModel[orderResourceKey]) {
          // Since there are several ways to change the order in checkout,
          // We need to reset this to always default to received data.
          this.model.length = 0
          this.model = merge(this.model, data.orderModel[orderResourceKey])
        }
      }
    })

    // ////////////
    // Singular //
    // ////////////

    this.on('deleteLineItem', (data, cb) => {
      logger('deleteLineItem', 'debug')()
      deleteLineItem(data)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })
  }
}

export const LineItemsInstance = new LineItems();